import React, { useMemo, useRef } from 'react';
import { useCompareKeyMetricsChart } from '../hooks';
import { dealWithMentionsSentimentChart, getGameColorOrName } from '../utils';
import { echartsContent, sortMapAuxiliaryFunc } from '@/pages/opinion/utils';
import { formatTimeXAxisText } from '@op/utils/chart';
import useLanguage from '@op/hooks/useLanguage';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import TableCommonHook from '@op/components/TableCommonHook';
import { SortBy } from 'tea-component/lib/table/addons';
import './index.scss';
import { formatDateFunc } from '@op/utils/time';
import TableColumn from '../component/TableColumn';
import CardCommon from '@op/components/CardCommon';
import { getGameDataIndex } from '@op/components/TableCommonHook/hooks/useCompareKeyMetricsColumns';
import { TableHeight } from '../constants';
import ProgressColumn from '../component/ProgressColumn';

export default function PRCharts({
  filter,
  chartType = 'line',
  gameData,
  sortBy,
  setSorts,
}: {
  filter: any;
  chartType?: string;
  gameData: {
    [id: string]: {
      name: string;
      color: string;
    };
  }[];
  sortBy: SortBy<any>;
  setSorts?: React.Dispatch<React.SetStateAction<SortBy<any>>>;
}) {
  const { formatMessage } = useLanguage();

  const getGameData = getGameColorOrName(gameData);

  const params = useMemo(
    () => ({
      ...filter,
      stats_types: ['pr'],
      tab_type: 'pr',
    }),
    [filter],
  );

  const { data: statistics, loading } = useCompareKeyMetricsChart({
    filter: params,
    dealWithToChart: dealWithMentionsSentimentChart,
  });

  const xAxisIntervalLimit = 8;
  const chartRef = useRef(null);
  const { xAxisData, yAxisData, tableData } = statistics;
  const hasStatistics = Array.isArray(yAxisData) && yAxisData.length > 0;

  const columns = useMemo(
    () => [
      {
        header: formatMessage({ id: 'Date' }),
        key: 'time',
        align: 'left',
        render: (cvm: any) => formatDateFunc(params?.date_type)(cvm.time),
      },
      {
        header: formatMessage({ id: 'Game' }),
        key: 'name',
        align: 'left',
        render: (cvm: any) => {
          const item: any = getGameData(cvm?.name, 'all');
          return <TableColumn name={item?.entity_name} item={item} />;
        },
      },
      {
        header: formatMessage({ id: 'All Articles' }),
        key: 'value',
        align: 'center',
        render: (cvm: any) => (
          <ProgressColumn
            percentage={cvm.ratio}
            value={cvm.value}
            oldTime={[]}
            newTime={[]}
            oldValue={''}
          ></ProgressColumn>
        ),
      },
    ],
    [formatMessage, params?.date_type],
  );

  const options = useMemo(() => {
    if (hasStatistics) {
      let legendData: {
        name: string;
        itemStyle: {
          color: string;
        };
      }[] = [];
      let seriesData: {
        itemStyle?: any;
        barMaxWidth?: string;
        barGap?: string;
        label?: any;
        name: string;
        type: string;
        stack?: string;
        data: (number | null)[];
      }[] = [];
      yAxisData.map((item) => {
        const color = getGameData(item.name, 'color');
        seriesData.push({
          name: item.name,
          type: chartType,
          stack: chartType === 'bar' ? 'total' : '',
          data: item.data,
          barMaxWidth: '70px',
          itemStyle: { color },
        });

        legendData.push({
          name: item.name,
          itemStyle: { color },
        });
      });

      const sortMap = getGameDataIndex(gameData);
      legendData.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
      const option = {
        tooltip: {
          trigger: 'axis',
          borderWidth: 0,
          padding: 0,
          confine: true,
          extraCssText: 'z-index:8',
          formatter: (data: any[]) => {
            const sumKey = data[0].axisValue;

            let timeText = '';
            if (params?.date_type) {
              timeText = formatDateFunc(params?.date_type)(sumKey);
            }
            return formatChartParamsDate({
              params: data,
              title: timeText,
              rightTitle: formatMessage({ id: 'All Articles' }),
              listTitleFunc: (item) => getGameData(item.seriesName),
              isSort: true,
            });
          },
        },
        legend: {
          data: legendData,
          formatter: (params: string) => getGameData(params),
        },
        axisPointer: getAxisPointer((data) =>
          formatTimeXAxisText(data.value, params?.date_type),
        ),
        xAxis: {
          type: 'category',
          boundaryGap: ['20%', 0],
          data: xAxisData,
          axisLabel: {
            interval: xAxisData?.length > xAxisIntervalLimit ? 'auto' : 0,
            fontSize: 10,
            showMaxLabel: true,
            formatter: (value: any) =>
              formatTimeXAxisText(value, params?.date_type),
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            fontSize: 11,
          },
        },
        series: seriesData,
      };
      return option;
    }
    return {};
  }, [
    hasStatistics,
    yAxisData,
    xAxisData,
    chartType,
    getGameData,
    params?.date_type,
    formatMessage,
  ]);

  return (
    <div ref={chartRef} className="dbo-pr-chart">
      <CardCommon
        contentCard
        loading={loading}
        loadingHeight={TableHeight.CHART_LOADING}
      >
        {chartType !== 'table' ? (
          <EChartCommon
            options={options}
            loading={loading}
            optionsProps={{ type: chartType }}
            empty={!hasStatistics}
            height={TableHeight.CHART}
            leftTitle={formatMessage({ id: 'All Articles' })}
          />
        ) : (
          <TableCommonHook
            recordKey={'comment_uin'}
            records={tableData || []}
            total={tableData?.length || 0}
            columns={columns}
            sortColumns={['time', 'value']}
            hasPageable={false}
            defaultSort={sortBy}
            bordered="all"
            minWidth="auto"
            changeSort={setSorts}
            sortType={'local'}
            maxHeight={TableHeight.CHART_TABLE}
          />
        )}
      </CardCommon>
    </div>
  );
}
